import { useState } from "react";
import { PageLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Video, Calendar, Clock, Users, PlayCircle, FileText,
  ChevronRight, Plus, Mic, MicOff, VideoOff, Captions, Eye, Download
} from "lucide-react";

const SAMPLE_UPCOMING = [
  {
    id: "c1",
    title: "QA Foundations: Test Plan Walkthrough",
    cohort: "Cohort 6 · QA Bootcamp",
    instructor: "Tara May",
    date: "Tomorrow",
    time: "10:00 AM – 11:30 AM CT",
    enrolled: 14,
    confirmed: 11,
    meet_url: "https://meet.google.com/abc-defg-hij",
    can_join_minutes: 15,
  },
  {
    id: "c2",
    title: "Cybersecurity Lab: Intro to Burp Suite",
    cohort: "Cohort 5 · Cyber Foundations",
    instructor: "Daniel Park",
    date: "Thu, Feb 27",
    time: "2:00 PM – 3:30 PM CT",
    enrolled: 9,
    confirmed: 8,
    meet_url: "https://meet.google.com/xyz-uvw-rst",
  },
  {
    id: "c3",
    title: "Career Coaching Drop-In",
    cohort: "All Active Participants",
    instructor: "Maya Singh",
    date: "Fri, Feb 28",
    time: "11:00 AM – 12:00 PM CT",
    enrolled: 32,
    confirmed: 22,
    meet_url: "https://meet.google.com/mno-pqrs-tuv",
  },
];

const SAMPLE_PAST = [
  {
    id: "p1",
    title: "QA Foundations: Bug Reporting Best Practices",
    date: "Feb 19, 2026",
    duration: "1h 28m",
    attendance: "12 of 14",
    recording_url: "#",
    transcript_url: "#",
  },
  {
    id: "p2",
    title: "QA Foundations: Welcome & Syllabus Overview",
    date: "Feb 12, 2026",
    duration: "1h 15m",
    attendance: "14 of 14",
    recording_url: "#",
    transcript_url: "#",
  },
];

const ScheduleClassMock = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Video className="w-5 h-5 text-[#1E3A8A]" /> Schedule a Live Class
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm text-slate-600">Class title</label>
            <input className="input-base" defaultValue="QA Foundations: Automation Intro" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-slate-600">Date</label>
              <input type="date" className="input-base" defaultValue="2026-03-04" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-slate-600">Time</label>
              <input type="time" className="input-base" defaultValue="10:00" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-slate-600">Cohort</label>
            <select className="input-base">
              <option>Cohort 6 · QA Bootcamp (14 students)</option>
              <option>Cohort 5 · Cyber Foundations (9 students)</option>
            </select>
          </div>
          <div className="bg-[#FFD507]/10 border border-[#FFD507]/30 rounded-lg p-3">
            <p className="text-xs text-slate-700">
              ✓ Google Meet link will be auto-generated<br />
              ✓ Calendar invites sent to enrolled students<br />
              ✓ Recording auto-saved to LMS · Captions on
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="btn-primary">Create Class</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const LiveClassesMockup = () => {
  const [scheduling, setScheduling] = useState(false);

  return (
    <div className="relative min-h-screen">
      {/* Mockup overlay banner */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-[#FFD507] to-[#FCC100] text-slate-900 px-6 py-3 shadow-md" data-testid="mockup-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-900 text-[#FFD507] flex items-center justify-center font-bold text-sm">
              👁
            </div>
            <div>
              <p className="font-bold text-sm uppercase tracking-wide">Preview Mockup — Instructor Logs In Here</p>
              <p className="text-xs text-slate-700">This is a visual prototype of the proposed Google Meet integration. Nothing here is live yet.</p>
            </div>
          </div>
          <Badge className="bg-slate-900 text-[#FFD507] border-0">Not connected</Badge>
        </div>
      </div>

      <PageLayout
        title="Live Classes"
        subtitle="Host live sessions, take attendance, and manage recordings — all powered by Google Meet."
      >
        <div className="space-y-8" data-testid="live-classes-mockup">
          {/* Stats row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: "Upcoming this week", value: "3", icon: Calendar },
              { label: "Total enrolled", value: "55", icon: Users },
              { label: "Avg attendance", value: "87%", icon: Eye },
              { label: "Recordings library", value: "24", icon: PlayCircle },
            ].map((s, i) => (
              <Card key={i} className="card-surface">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
                      <p className="text-3xl font-bold text-[#1E3A8A] mt-1">{s.value}</p>
                    </div>
                    <s.icon className="w-6 h-6 text-[#FFD507]" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Schedule action */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900">Upcoming Classes</h2>
              <p className="text-sm text-slate-500">Auto-generated Meet links · Calendar invites · Recording on</p>
            </div>
            <Button
              className="btn-primary flex items-center gap-2"
              onClick={() => setScheduling(true)}
              data-testid="mock-schedule-btn"
            >
              <Plus className="w-4 h-4" /> Schedule a Class
            </Button>
          </div>

          {/* Upcoming classes list */}
          <div className="grid gap-4">
            {SAMPLE_UPCOMING.map((c, idx) => (
              <Card key={c.id} className="card-surface hover:shadow-md transition-shadow" data-testid={`upcoming-${c.id}`}>
                <CardContent className="pt-5 pb-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-[#1E3A8A] bg-[#1E3A8A]/10 px-2 py-0.5 rounded-full">{c.cohort}</span>
                        {idx === 0 && (
                          <span className="text-xs font-bold bg-[#FFD507] text-slate-900 px-2 py-0.5 rounded-full animate-pulse">
                            Starts soon
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-heading font-bold text-slate-900">{c.title}</h3>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {c.date}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {c.time}</span>
                        <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.confirmed}/{c.enrolled} confirmed</span>
                        <span className="text-slate-500">Host: {c.instructor}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="sm">View Roster</Button>
                      <Button
                        className={idx === 0 ? "bg-[#FFD507] hover:bg-[#FCC100] text-slate-900 font-semibold flex items-center gap-2" : "btn-primary flex items-center gap-2"}
                        size="sm"
                      >
                        <Video className="w-4 h-4" /> {idx === 0 ? "Join Meet" : "Open Meet"}
                      </Button>
                    </div>
                  </div>

                  {/* Comfort settings strip - ties to Working Style Quiz */}
                  {idx === 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Class comfort settings</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-full"><VideoOff className="w-3 h-3" /> Camera-off welcomed</span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-full"><Captions className="w-3 h-3" /> Captions auto-on</span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-full"><MicOff className="w-3 h-3" /> Chat-only Q&amp;A allowed</span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FFD507]/15 border border-[#FFD507]/40 text-[#1E3A8A] rounded-full font-medium">3 students prefer no breakouts</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Past sessions / Recordings library */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Recent Recordings</h2>
              <Button variant="outline" size="sm">View all 24 →</Button>
            </div>
            <div className="grid gap-3">
              {SAMPLE_PAST.map((p) => (
                <Card key={p.id} className="card-surface" data-testid={`past-${p.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                          <PlayCircle className="w-5 h-5 text-[#1E3A8A]" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{p.title}</p>
                          <p className="text-xs text-slate-500">{p.date} · {p.duration} · Attendance {p.attendance}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex items-center gap-1.5"><PlayCircle className="w-4 h-4" /> Watch</Button>
                        <Button variant="outline" size="sm" className="flex items-center gap-1.5"><FileText className="w-4 h-4" /> Transcript</Button>
                        <Button variant="outline" size="sm" className="flex items-center gap-1.5"><Download className="w-4 h-4" /> Download</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* What's included card */}
          <Card className="card-surface bg-gradient-to-br from-[#1E3A8A] to-[#152C6E] text-white border-0">
            <CardContent className="pt-6 pb-6">
              <p className="text-[#FFD507] text-xs uppercase tracking-wider font-bold mb-2">When Live</p>
              <h3 className="text-xl font-heading font-bold mb-3">Everything wired into Aspiritech Academy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-white/90">
                <p>✓ Auto-create Meet link on schedule (Google Calendar API)</p>
                <p>✓ Calendar invites sent to enrolled students automatically</p>
                <p>✓ Cloud recording → saved on the class doc → visible in LMS</p>
                <p>✓ Live captions on by default for accessibility</p>
                <p>✓ Attendance auto-tracked from Meet → updates engagement metrics</p>
                <p>✓ "Class Comfort Settings" pulled from each student's Working Style Quiz</p>
                <p>✓ "Join Class" button shows on student dashboard 15 min before start</p>
                <p>✓ Audit-logged for compliance</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageLayout>

      <ScheduleClassMock open={scheduling} onClose={() => setScheduling(false)} />
    </div>
  );
};

export default LiveClassesMockup;
